/**
 * Dependency-free fuzzy matching, shared by server and frontend.
 *
 * A query matches a target when every query character appears in the target
 * in order (subsequence match). The score rewards consecutive runs, matches
 * at word boundaries, and matches near the start of the target.
 */

import type { Tool } from "./types";

/** Returns a score > 0 when `query` fuzzy-matches `target`, otherwise 0. */
export function fuzzyScore(query: string, target: string): number {
  const q = query.toLowerCase();
  const t = target.toLowerCase();
  if (q.length === 0) return 0;
  if (q.length > t.length) return 0;

  let score = 0;
  let ti = 0;
  let prevMatchIndex = -2;

  for (let qi = 0; qi < q.length; qi++) {
    const found = t.indexOf(q[qi]!, ti);
    if (found === -1) return 0;

    let charScore = 1;
    if (found === prevMatchIndex + 1) charScore += 2; // consecutive run
    if (found === 0) charScore += 3; // start of target
    else if (t[found - 1] === " " || t[found - 1] === "-" || t[found - 1] === "_") {
      charScore += 2; // word boundary
    }

    score += charScore;
    prevMatchIndex = found;
    ti = found + 1;
  }

  // Prefer tighter matches: penalize by how spread-out the match is.
  const spread = prevMatchIndex - (t.indexOf(q[0]!) ?? 0);
  score += Math.max(0, q.length * 2 - spread);

  // Exact substring gets a strong boost.
  if (t.includes(q)) score += q.length * 3;

  return score;
}

/** Field weights: name matches count far more than description matches. */
export function scoreTool(query: string, tool: Tool): number {
  const nameScore = fuzzyScore(query, tool.name) * 4;
  const categoryScore = Math.max(0, ...tool.categories.map(c => fuzzyScore(query, c))) * 2;
  const keywordScore = Math.max(0, ...(tool.keywords ?? []).map(k => fuzzyScore(query, k))) * 2;
  const descriptionScore = fuzzyScore(query, tool.description);
  return Math.max(nameScore, categoryScore, keywordScore, descriptionScore);
}

/**
 * Returns tools matching `query`, best match first.
 * An empty query returns all tools alphabetized by name (per docs/DESIGN.md).
 */
export function searchTools(tools: Tool[], query: string): Tool[] {
  const trimmed = query.trim();
  if (trimmed === "") {
    return [...tools].sort((a, b) => a.name.localeCompare(b.name));
  }
  return tools
    .map(tool => ({ tool, score: scoreTool(trimmed, tool) }))
    .filter(entry => entry.score > 0)
    .sort(
      (a, b) =>
        b.score - a.score ||
        Number(b.tool.featured ?? false) - Number(a.tool.featured ?? false) ||
        a.tool.name.localeCompare(b.tool.name),
    )
    .map(entry => entry.tool);
}
